import { useId, type ReactNode } from 'react';

import { EmptyState } from '@/components/feedback/EmptyState';
import { cn } from '@/lib/utils/cn';

/** Carte de la fiche : masque les rubriques vides, ou affiche leur état vide explicite. */
export const DetailSection = ({
  title,
  count,
  isEmpty = false,
  children,
  className,
  icon,
  emptyMessage,
  action,
  id,
}: {
  title: string;
  count?: number;
  isEmpty?: boolean;
  children: ReactNode;
  className?: string;
  icon?: ReactNode;
  emptyMessage?: string;
  action?: ReactNode;
  id?: string;
}) => {
  const headingId = useId();
  if (isEmpty && !emptyMessage) return null;

  return (
    <section
      id={id}
      aria-labelledby={headingId}
      className={cn(
        'border-navy-200/70 min-w-0 overflow-hidden rounded-[10px] border bg-white shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95),0_4px_16px_-8px_rgb(15_42_95/0.1)]',
        className,
      )}
    >
      <div className="border-navy-100 bg-navy-50/40 flex items-center gap-3 border-b bg-linear-to-b from-white/90 to-transparent px-5 py-4">
        {icon ? (
          <span
            aria-hidden
            className="border-navy-200/60 text-navy-500 grid size-8 shrink-0 place-items-center rounded-lg border bg-white text-base shadow-[inset_0_1px_0_0_white]"
          >
            {icon}
          </span>
        ) : null}
        <h2
          id={headingId}
          className="text-navy-900 flex min-w-0 flex-1 items-center gap-2 text-[14px] font-semibold"
        >
          {title}
        </h2>
        {count !== undefined ? (
          <span className="border-navy-100 text-navy-500 rounded-md border bg-white px-2 py-0.5 text-xs tabular-nums">
            {count}
          </span>
        ) : null}
        {action}
      </div>
      {isEmpty ? (
        <EmptyState
          title={emptyMessage ?? 'Aucune information'}
          className="py-9 [&>p]:text-sm [&>p]:font-normal [&>p]:text-ink-500"
        />
      ) : (
        <div className="p-5">{children}</div>
      )}
    </section>
  );
};

/**
 * Ligne « libellé / valeur », **omise si la valeur est vide**.
 *
 * Retourner `null` plutôt qu'afficher un tiret : dans une fiche où trois
 * champs sur quatre sont vides, une colonne de tirets n'informe de rien et
 * noie les quelques valeurs présentes.
 */
export const Field = ({ label, value }: { label: string; value: ReactNode }) => {
  if (value === null || value === undefined || value === '') return null;

  return (
    <div className="min-w-0">
      <dt className="text-ink-500 text-xs">{label}</dt>
      <dd className="text-navy-900 mt-1.5 text-sm leading-6 wrap-anywhere">{value}</dd>
    </div>
  );
};

/** Grille de `Field`, responsive. */
export const FieldGrid = ({ children }: { children: ReactNode }) => (
  <dl className="grid grid-cols-1 gap-x-6 gap-y-5 sm:grid-cols-2">{children}</dl>
);
